// Lesson content for /sewing-primitives.
//
// The curriculum is the thesis of the page written down: learn a handful of
// primitives (how their flat patterns fold into 3D), learn what the sliders
// do to those patterns, then build anything by composing primitives — the
// same "primitives first" ladder as 3D modeling or drawing, applied to
// fabric. Kept as plain data so the page just renders it and tracks
// per-check progress with stable ids.

// Each lesson: { id, title, shape?, goal, body: [paragraphs], tryThis: [..],
// checks: [{ id, text }] }. `shape` links a lesson to the lab's shape picker.

export const LESSONS = [
  {
    id: "flat-to-form",
    title: "Flat → form",
    shape: null,
    goal: "Understand the one move sewing actually has: joining two edges turns flat fabric into a fold, and stuffing turns folds into form.",
    body: [
      "Every plush ever made is the same trick repeated: cut flat pieces, sew edges together, flip it right-side out, stuff it. A seam is an edge-gluing — exactly what you do when you fold a paper net into a cube, except fabric lets the fold be curved.",
      "That's why 3D-modeling instincts transfer. A pattern is literally a UV unwrap you can iron. If you can look at a shape and imagine slicing it open and flattening it, you can draft it.",
      "Two squares sewn together around the edge and stuffed is the degenerate case — the 'hello world' pillow. No shaping at all, and the stuffing still forces it into a puffy lens. Remember that: stuffing always fights for the roundest shape it can get. Everything sharper than a blob has to be earned with seams.",
    ],
    tryThis: [
      "Take a piece of paper, tape two edges together, and notice you've already made 3D. That's the whole medium.",
      "In the lab, drag the fold slider slowly from flat to sewn on any shape and watch which edges find each other.",
    ],
    checks: [
      { id: "l0-machine", text: "Thread the machine and sew a straight seam on scrap fabric (this is 80% of the battle)" },
      { id: "l0-pillow", text: "Sew the hello-world pillow: two squares, right sides together, leave a gap, flip, stuff, close" },
    ],
  },
  {
    id: "box",
    title: "The box",
    shape: "box",
    goal: "Sew your first real primitive: all straight seams, and corners that emerge where three seams meet.",
    body: [
      "The box is the friendliest primitive because its pattern is its geometry with zero distortion — six rectangles, drawn at exactly their finished size. The cross-shaped net folds up on hinge lines and every colored edge pair meets its partner.",
      "Notice what the sliders do in the lab: changing width, depth, or height never changes the *kind* of pattern, only the numbers on the rectangles. A mattress, a brick, a die, and a plush microwave body are the same pattern with different measurements. That's the primitive mindset.",
      "The catch fabric adds: stuffing pressure rounds corners off. A sharp box wants firm stuffing, slightly oversized corners, or a stiffer fabric. A soft marshmallowy box just wants you to relax.",
      "And here's the twist the lab's construction picker teaches: the SHAPE doesn't dictate the PATTERN. The same box can be one hinged cross, a strip plus two lids (a box is secretly a square cylinder), six separate panels, or — wildest of all — a plain flat pillow with its corners pinched and sewn across. Same geometry, four different piles of fabric, four different kinds of corner. Experienced sewists pick a construction to match the corner they want.",
    ],
    tryThis: [
      "Slide height way down — you drafted a mattress. Way up — a tower. Set all three equal — a die.",
      "Watch the seven seam colors: each appears exactly twice on the flat cross. Those two edges are the same edge of the finished box.",
      "Cycle the four constructions in the lab and watch the same box get built four ways. The boxed pillow is the one to stare at: the pattern looks nothing like the shape.",
    ],
    checks: [
      { id: "l1-draft", text: "Draft a box net by hand on paper for a 10 × 6 × 4 cm box (don't forget seam allowance)" },
      { id: "l1-cube", text: "Sew a cube ~8 cm on a side" },
      { id: "l1-remix", text: "Sew a second box with the same net, different numbers (a brick, a bar of gold, a tofu)" },
      { id: "l1-corners", text: "Sew a boxed-corner pillow: plain rectangle pillow, pinch each corner flat, sew straight across" },
    ],
  },
  {
    id: "pyramid",
    title: "The pyramid",
    shape: "pyramid",
    goal: "Learn the first non-obvious measurement: pattern pieces are drawn at their SLANT size, not their silhouette size.",
    body: [
      "A pyramid's side triangle is taller than the pyramid. The pattern needs the distance along the fabric — up the sloped face — which is √(h² + (b/2)²), not h. This is the single most important drafting idea there is, and it never stops applying.",
      "Play with the height slider and watch the triangles: a tall pyramid wants long skinny triangles that barely fold in; a squat one wants stubby triangles that fold way past vertical. The fold angle is doing the 3D work; the flat pattern just quietly gets longer or shorter.",
    ],
    tryThis: [
      "Set height very low and watch the net become almost a flat star — that's why low pyramids read as 'pillows with attitude.'",
      "Compare the triangle height in the pattern notes to the h slider. They're never equal.",
    ],
    checks: [
      { id: "l2-slant", text: "Compute the face triangle height for a pyramid with b = 12, h = 9 by hand, then check it against the lab" },
      { id: "l2-sew", text: "Sew a little pyramid (great scrap-buster; it stands up and looks smug)" },
    ],
  },
  {
    id: "cylinder",
    title: "The cylinder",
    shape: "cylinder",
    goal: "Meet the first curved seam, and the first pattern dimension that is computed, not chosen: circumference.",
    body: [
      "A cylinder is a rectangle that rolls up plus two lids. You choose the radius and the height; the wall's width is then decided for you — 2πr, the circumference. If the circle's edge and the wall's edge don't match to within a few millimeters, the fabric will pucker (wall too long) or gape (wall too short).",
      "Sewing a circle to a straight edge is the first real skill jump: you're joining a curved line to a straight one of equal length. Go slow, pin generously (or clip), and ease the fabric around. Clipping small notches into the seam allowance of the curve lets it relax.",
      "Cylinders are everywhere once you see them: mugs, cans, logs, plush arms and legs, tree trunks, lipstick, batteries. This one pattern is half of all plush anatomy.",
    ],
    tryThis: [
      "Drag the radius slider and watch the wall stretch — the pattern view updates 2πr live. The math is the pattern.",
      "Scrub the fold slider slowly: the rectangle rolls without stretching. Fabric, like paper, bends but doesn't stretch (much) — that's why cylinders and cones are 'easy' curves.",
    ],
    checks: [
      { id: "l3-mug", text: "Measure a real mug (radius and height) and draft its wall width by hand" },
      { id: "l3-can", text: "Sew a little can/log — one wall, two lids" },
    ],
  },
  {
    id: "cone",
    title: "The cone",
    shape: "cone",
    goal: "Learn the wildest unfold in the primitive kit: a cone is a pizza slice of a bigger circle.",
    body: [
      "Unroll a cone and you don't get a triangle — you get a sector (pizza slice) whose radius is the SLANT length √(r² + h²) and whose angle is 360° × r/s. Tall skinny cone: tiny slice of a huge circle. Flat squat cone: almost the whole circle with a nibble missing.",
      "This is the pyramid lesson (slant, not height) and the cylinder lesson (edge lengths must match: the arc is exactly 2πr) fused into one shape. If you can draft a cone from r and h without looking anything up, you've internalized both.",
      "Cones in the wild: carrots, party hats, ice cream cones, witch hats, rocket noses, claws, teeth, mushroom stems that taper. Also: a cone with the tip cut off (a frustum) is a bucket, a lampshade, a plant pot — same sector, trimmed.",
    ],
    tryThis: [
      "Slide height toward 1 and watch the sector angle crawl toward 360° — a flat cone is just a circle.",
      "Make it tall and skinny and watch the slice get narrow. The 3D shape and the flat pattern move in opposite directions — that's the intuition to collect.",
    ],
    checks: [
      { id: "l4-draft", text: "Draft a cone from r = 4, h = 12 by hand: compute s and the sector angle, then check against the lab" },
      { id: "l4-carrot", text: "Sew a carrot (a long thin cone + embroidered face, optional but correct)" },
    ],
  },
  {
    id: "sphere",
    title: "The sphere",
    shape: "sphere",
    goal: "Confront the truth: no flat piece folds into a ball. Learn gores, the workaround behind every plush planet.",
    body: [
      "Boxes, cylinders and cones are 'developable' — paper can do them, fabric does them crisply. A sphere is not: it curves in two directions at once, so any flat pattern is an approximation. The classic approximation is gores: petal-shaped strips (width follows cos of latitude) sewn side by side like a beach ball.",
      "The gore count is a quality dial. Four gores + stuffing = a slightly lumpy ball (stuffing rounds a LOT). Twelve gores = smooth, but three times the seams. Most plush balls are 6–8 gores because stuffing is doing half the work for free.",
      "Darts — the little pinches you see in clothing — are micro-gores: each dart removes a wedge of flat fabric to create curvature right there. Once you see gores, you see darts; once you see darts, you can add roundness anywhere on any primitive.",
    ],
    tryThis: [
      "Set gores to 3, then 12, and scrub the fold slider on both. Watch how much the flat petals have to 'lie' at low counts.",
      "Note the petal width formula 2πr/n — the gores are just the cylinder wall sliced vertically and tapered by cos(latitude).",
    ],
    checks: [
      { id: "l5-ball", text: "Sew a 6-gore ball (~10 cm). Expect the poles to be fiddly; everyone's poles are fiddly" },
      { id: "l5-dart", text: "Sew one dart into a flat scrap and watch it become a shallow cone — that's curvature on demand" },
    ],
  },
  {
    id: "darts",
    title: "The dart",
    shape: "dart",
    goal: "Learn the smallest unit of shaping there is: remove a wedge, sew the cut shut, and flat fabric domes on command.",
    body: [
      "The sphere lesson ended with a teaser: darts are micro-gores. Here's the whole move by itself. Cut a wedge out of flat fabric (or just fold one away), sew the two cut edges together, and the fabric can't stay flat — the circumference you removed has to go somewhere, so it becomes height. You didn't cut any curve; the curvature appeared out of the pinch.",
      "The lab's dart machine makes the accounting visible. Each dart 'bites' an angle out of the disc. Total the bites and you know the dome: more bite, taller dome. Bite everything (360°'s worth) and you'd have a cone — which is why the cone's pizza-slice pattern should suddenly look like one giant dart.",
      "Reach is the subtler dial: a dart that stops partway leaves the center flat and curves only the rim — a beret. A dart that runs deep curves everything — a beanie. Fitted clothing is this exact machine pointed at a body: bust darts, waist darts, elbow darts are all wedges of nothing, placed where the body needs the fabric to leave flatland.",
      "For plush work, darts are how you add roundness LOCALLY without redrafting the whole pattern: a dart at the chin, two at the cheeks, one under the tail. Anywhere a shape needs a bump, you can pinch one into existence.",
    ],
    tryThis: [
      "Set darts to 2 and bite to 30° — that's a classic beret construction. Now 8 darts × 8° — same total curvature, distributed smoother.",
      "Scrub reach and watch the beret/beanie trade-off: where the darts stop is where the curve stops.",
      "Watch the seam colors while folding: each dart's two cut edges are one colored V on the disc, and they zip shut into a single line.",
    ],
    checks: [
      { id: "l11-beret", text: "Cut a fabric circle, sew 4 darts into it, and watch it dome — compare with the lab's height prediction" },
      { id: "l11-spot", text: "Find three darts on clothing you own (check waists, busts, elbows) and read each one as a wedge of removed flat" },
    ],
  },
  {
    id: "lathe",
    title: "The lathe",
    shape: "lathe",
    goal: "Generalize the sphere: gores fit ANY silhouette you can spin — egg, teardrop, pear. This is where creature bodies come from.",
    body: [
      "In 3D modeling, a lathe spins a side-view curve around an axis to make a vase, an egg, a chess pawn. Fabric has the same tool and it's the gores you already know: the sphere's petals work for any silhouette, not just a semicircle. Gore length = the silhouette's arc (measure along the curve, not the height). Gore width at every latitude = that latitude's circumference ÷ the number of gores. Two rules, any body.",
      "Check the sanity case in the lab: set the silhouette to 'ball' and you get exactly the sphere lesson back. Then switch to the egg and watch the petals go asymmetric — fatter near the bottom, longer through the taper. The pattern is a portrait of the silhouette drawn in circumference.",
      "This is the single most load-bearing primitive in plush anatomy. Practically every commercial stuffed-animal body and head is a lathe with a personality: an egg on its side, a pear with ears, a teardrop with a face. The creature generator (on the make page) is exactly this machine wearing semantic sliders.",
    ],
    tryThis: [
      "Set the silhouette to ball and confirm the sphere comes back — same petals, same πr length.",
      "Switch between egg and pear at the same width and height, and watch ONLY the petal's waistline move. Silhouette = width-over-height, nothing else.",
      "Try the bowling pin with 4 gores, then 12. Low counts lie hardest exactly where the silhouette curves fastest — the neck.",
    ],
    checks: [
      { id: "l12-egg", text: "Sew a 6-gore egg (~10 cm tall) — the first plush body that isn't a ball" },
      { id: "l12-trace", text: "Trace a real object's silhouette (a pear, a vase), measure its arc with string, and draft one gore by hand" },
    ],
  },
  {
    id: "pancake",
    title: "The pancake",
    shape: "pancake",
    goal: "Meet the softest primitive: any outline, cut twice, sewn around, stuffed. Zero drafting — and learn what that costs you.",
    body: [
      "Everything so far drafted the third dimension on purpose. The pancake doesn't: you draw ONE flat outline, cut two, sew around the edge (leave a gap), flip, stuff. The stuffing supplies all of the 3D. This is where most beginner plushies actually live — it's the hello-world pillow wearing a costume.",
      "The trade is control. You don't choose the thickness: stuffing puffs the widest parts most, corners and skinny bits stay flat. Scrub the stuffing slider in the lab and watch the deal you're making — a star's points barely inflate while its middle balloons. That's not a bug; a plush star WANTS flat points.",
      "For asymmetric outlines (the fish), cut the second panel mirrored — right sides together means one of them is facing the wrong way. Every beginner learns this by sewing two left-facing fish. Now you don't have to.",
    ],
    tryThis: [
      "In the lab, set stuffing to 0 and fold — a flat sandwich. Now scrub it up and watch thickness appear where the outline is fat.",
      "Compare the star and the blob at full stuffing: same construction, wildly different puff. Outline width IS the thickness dial.",
    ],
    checks: [
      { id: "l9-fish", text: "Sew a pancake fish (remember to mirror the second panel)" },
      { id: "l9-observe", text: "Stuff one pancake hard and one soft — feel how stuffing pressure changes the silhouette" },
    ],
  },
  {
    id: "gusset",
    title: "The gusset & the perimeter rule",
    shape: "gusset",
    goal: "Add a strip between two profiles to buy back the control the pancake gave up — and learn the most general rule in pattern drafting.",
    body: [
      "Take the pancake's two panels and pull them apart. Sew a long strip between them, running all the way around. Now depth is a decision again: the strip's width is the object's thickness. This is the gusset, and it makes bread loaves, whales, camera bags, gameboys — any 'extruded blob' with softly rounded edges.",
      "Here's the rule that runs the whole curriculum, in its final form: the strip's LENGTH is not yours to choose. It's the profile's perimeter, exactly — just like the cylinder wall was 2πr, except now the 'circle' is any shape you like. Any two edges that sew together must be the same length. That's the entire theory of pattern drafting in one sentence.",
      "This is also how real stuffed animals are drafted: a teddy's head is two side profiles plus a head gusset running nose to nape. Once you can read profile + gusset, commercial patterns stop looking like magic and start looking like this lab.",
      "Practical notes: measure a curved profile's perimeter with a string (or trust the lab's number). Pin the quarter marks first, ease between them — same skill as setting a circle into a cylinder, because it IS that skill, generalized.",
    ],
    tryThis: [
      "Switch profiles in the lab and watch the strip length recompute — the shape changes, the rule doesn't.",
      "Crank depth way up on the loaf: you're drafting a sandwich loaf. Way down: a slice of toast. Same profile, one decision.",
    ],
    checks: [
      { id: "l10-string", text: "Draw any closed shape on paper and measure its perimeter with a string — that's a gusset strip length" },
      { id: "l10-loaf", text: "Sew the bread loaf: two profiles + one strip, quarter marks pinned first" },
      { id: "l10-read", text: "Find a real plush pattern online and identify its gussets (head gusset, belly gusset...)" },
    ],
  },
  {
    id: "remix",
    title: "Remixing primitives",
    shape: null,
    goal: "Stop treating the primitives as five shapes — treat them as five families you can stretch, taper, squash, and trim.",
    body: [
      "Everything the sliders taught you is the remix rulebook: scaling a primitive never changes its pattern's structure, only its numbers. Beyond scaling: taper a cylinder and its wall rectangle becomes a curved band (that's the cone continuum). Cut a cone's tip: frustum → bucket, lampshade. Halve a sphere: hemisphere cap → mushroom cap, dome, boob (it comes up more than you'd think in plush work).",
      "Squash-and-gather is the other big move: a sphere gathered at intervals becomes a pumpkin; a cylinder pinched at the middle becomes a peanut. These aren't new patterns — they're primitives plus a seam or a gathering thread.",
      "The skill to practice: look at the pattern view while you move a slider and predict what will change before it does. When your predictions are boring and correct, you've graduated.",
    ],
    tryThis: [
      "In the lab, morph the cone from tall (h=20) to flat (h=1) and narrate what the sector does. Out loud. The cat won't judge.",
      "Sketch (on paper) the pattern for a bucket: a cone's sector with the pointy end trimmed off, plus two different circles.",
    ],
    checks: [
      { id: "l6-frustum", text: "Draft and sew a frustum (plant pot / bucket) — a trimmed cone sector + 2 circles" },
      { id: "l6-predict", text: "For each shape in the lab, correctly predict the pattern change before touching a slider" },
    ],
  },
  {
    id: "joints",
    title: "Joints — three ways to attach",
    shape: null,
    goal: "Learn the attachment vocabulary: every plush ever assembled uses some mix of three structural joins (plus flat appliqué for details that should read as drawn on).",
    body: [
      "Once you can make primitives, composition is a solved problem with three tools. One: SEWN INTO A SEAM — the part is sandwiched between the parent's layers (pointing inward!) before the seam is sewn; turn the parent right-side out and the part pops out attached. This is how ears and tails work, and it's why they never fall off: they're structural.",
      "Two: SET INTO AN OPENING — cut a hole in the parent, sew the part's rim into it. This is the cylinder lesson between two objects: the rim and the opening must be the same length or you get puckers. Set-in limbs and snouts work this way.",
      "Three: LADDER STITCH — both parts finished and stuffed, then joined invisibly by hand. The most flexible and the most common. The rule of thumb for choosing: if you can plan it before sewing the parent, in-seam is strongest; if the part needs to sit proud of a surface, ladder; if it should emerge FROM the surface, socket.",
      "Order matters more than ever here: in-seam attachments happen in the MIDDLE of making the parent, so a real project plan interleaves — you can't sew the head shut and then remember the ears. The assembly lab's teddy (on the make page) shows all three joints color-coded.",
    ],
    tryThis: [
      "In the assembly lab, pull the slider apart and follow each pair of colored dots — same color, same technique.",
      "Look at a real stuffed animal's ears. You will not find a ladder-stitch line: they're in the head seam. This is the moment the trick becomes visible everywhere.",
    ],
    checks: [
      { id: "l8-inseam", text: "Sew a fabric tab into a scrap seam (sandwich it, sew, flip) — feel how strong that join is" },
      { id: "l8-socket", text: "Cut a hole in a scrap piece and set a small tube into it, matching edge lengths" },
      { id: "l8-teddy", text: "Assemble the teddy: spheres + cylinders + all three joints. The graduation plush" },
    ],
  },
  {
    id: "compose",
    title: "Composing objects",
    shape: null,
    goal: "The graduation move: decompose any object into primitives, sew the primitives, attach them. Plush anything.",
    body: [
      "This is exactly box modeling from 3D graphics, in fleece. A plush microwave is a box with a shallow box door and three cylinder buttons. A mug is a cylinder plus a bent cylinder handle. A mushroom is a hemisphere on a fat cylinder. You are not inventing new geometry anymore — you're art-directing an assembly of solved problems.",
      "Practical order of operations: sew each primitive separately (leave stuffing gaps), stuff the big ones, then attach with ladder stitch (an invisible hand stitch that zips two finished pieces together). Small parts like buttons are often best sewn flat and appliquéd on rather than built as tiny 3D pieces.",
      "The composite gallery on the make page is a starter menu. The real assignment is the last check: pick any object within arm's reach and read it as primitives. Once you can't stop doing that, you can sew anything you can see.",
    ],
    tryThis: [
      "Pick an object on your desk right now and decompose it out loud into ≤ 4 primitives.",
      "For your first composite, pick one with two parts max (ice cream cone: cone + ball). Ambition is for the third project.",
    ],
    checks: [
      { id: "l7-decompose", text: "Write primitive breakdowns for five random objects around your home" },
      { id: "l7-icecream", text: "Sew the ice cream cone (cone + gored ball) — your first composite" },
      { id: "l7-microwave", text: "Sew the plush microwave. This is the final boss. Send me a picture (I am me)" },
    ],
  },
];

// Real-fabric footnotes — the stuff the clean math politely ignores.
export const REALITY_NOTES = [
  {
    title: "Seam allowance",
    text: "The math draws the finished shape. Add ~0.7–1 cm outside every cut edge for the seam itself — it is never included in the formulas here.",
  },
  {
    title: "Stuffing pressure",
    text: "Stuffing pushes everything toward a sphere. Corners round off, edges soften. For crisp shapes: firmer fabric, lighter stuffing, or slightly exaggerated corners.",
  },
  {
    title: "Fabric stretch",
    text: "Woven cotton barely stretches — it behaves like the paper model and is the best fabric to LEARN on. Minky/fleece stretch (usually crosswise): forgiving of wobbly seams, floppy about precision. Keep stretch directions consistent across pieces.",
  },
  {
    title: "Clip your curves",
    text: "Seam allowance fights curved seams. Snip little notches into the allowance (not through the stitches!) so circles and gores can relax when turned right-side out.",
  },
  {
    title: "Ladder stitch",
    text: "The invisible hand stitch that closes stuffing gaps and attaches finished parts. Learn it once; use it on literally every project forever.",
  },
  {
    title: "Paper first",
    text: "Tape a paper version of any pattern before cutting fabric. Paper is a free, honest preview of the developable parts — if the paper model won't close, the fabric won't either.",
  },
  {
    title: "Sewing order",
    text: "Seams have a sequence: keep the work flat as long as possible, sew in-seam attachments before closing their parent, and save one seam as the turning gap — it gets ladder-stitched shut by hand at the very end. The numbered seam keys in the lab follow this rule.",
  },
  {
    title: "Pin at the marks",
    text: "The little tick marks on curved pieces are registration marks: pin them to their partners first, then ease the fabric between pins. Circles go in flat; freehand circles go in crooked.",
  },
];

// Starter composite "recipes" — objects decomposed into the lab's primitives.
export const COMPOSITES = [
  {
    id: "icecream",
    name: "Ice cream cone",
    emoji: "🍦",
    difficulty: 1,
    parts: [
      { shape: "cone", note: "the cone (tall, narrow)" },
      { shape: "sphere", note: "the scoop (6–8 gores)" },
    ],
    plan: "Sew both, stuff both, ladder-stitch the ball into the cone's opening. First composite, two pieces, instant credibility.",
  },
  {
    id: "mushroom",
    name: "Toadstool",
    emoji: "🍄",
    difficulty: 2,
    parts: [
      { shape: "sphere", note: "cap = top half of a gored sphere" },
      { shape: "cylinder", note: "stem (fat, short)" },
      { shape: "cylinder", note: "gill disc = one extra circle under the cap" },
    ],
    plan: "Half-gores make the dome, a circle closes it underneath, the stem ladder-stitches to the middle. Appliqué spots.",
  },
  {
    id: "mug",
    name: "Mug",
    emoji: "☕",
    difficulty: 2,
    parts: [
      { shape: "cylinder", note: "body (open top if you're brave)" },
      { shape: "cylinder", note: "handle = thin cylinder bent into a C" },
    ],
    plan: "Body is the lesson-3 can. The handle is a skinny stuffed tube ladder-stitched on at both ends. Optional: a brown felt circle of 'coffee.'",
  },
  {
    id: "microwave",
    name: "Microwave",
    emoji: "📦",
    difficulty: 3,
    parts: [
      { shape: "box", note: "body (wide, shallow)" },
      { shape: "box", note: "door = very flat box, or appliqué panel" },
      { shape: "cylinder", note: "buttons/dial × 3 (tiny, or appliqué)" },
    ],
    plan: "The lesson-1 box at microwave proportions, a flat box (or stitched panel) for the door with a gray fabric window, button cylinders appliquéd on. Beep optional.",
  },
  {
    id: "chair",
    name: "Mini armchair",
    emoji: "🛋️",
    difficulty: 3,
    parts: [
      { shape: "box", note: "seat base" },
      { shape: "box", note: "backrest (tall, thin)" },
      { shape: "cylinder", note: "armrests × 2 (or thin boxes)" },
    ],
    plan: "Four stuffed boxes ladder-stitched into furniture. Squishy cylinders read as rolled armrests. Doll-scale interior design unlocked.",
  },
  {
    id: "teddy",
    name: "Teddy bear",
    emoji: "🧸",
    difficulty: 3,
    parts: [
      { shape: "sphere", note: "head + body (squash the body taller)" },
      { shape: "sphere", note: "muzzle + ears (tiny, flattened)" },
      { shape: "cylinder", note: "arms + legs × 4" },
    ],
    plan: "The assembly-lab flagship: ears go INTO the head seam, legs set into body openings, everything else ladder-stitched. Uses all three joints — see the joints lesson.",
  },
  {
    id: "rocket",
    name: "Rocket",
    emoji: "🚀",
    difficulty: 3,
    parts: [
      { shape: "cylinder", note: "body" },
      { shape: "cone", note: "nose cone" },
      { shape: "pyramid", note: "fins × 3–4 (flat pyramids or plain triangles)" },
    ],
    plan: "The whole primitive kit in one object: can + party hat + fins. Match the nose cone's base circle to the body's radius — the 2πr edges must agree.",
  },
];

export const ALL_CHECK_IDS = LESSONS.flatMap((l) => l.checks.map((c) => c.id));

export const PROGRESS_KEY = "sewingPrimitivesProgress";

// Progress lives in localStorage and is shared by every page of the
// sub-site (hub shows the tally, learn page owns the checkboxes).
export const loadProgress = () => {
  try {
    return JSON.parse(localStorage.getItem(PROGRESS_KEY)) || {};
  } catch {
    return {};
  }
};
